import { Injectable } from '@angular/core';
import { catchError, forkJoin, map, of, switchMap, throwError, type Observable } from 'rxjs';
import { silentApiRequestContext } from '../../core/api/api-request.interceptor';
import type { Agent } from '../../core/api/generated/models/Agent';
import type { AgentArchetypeListResponse } from '../../core/api/generated/models/AgentArchetypeListResponse';
import type { AgentBuilderContextRequest } from '../../core/api/generated/models/AgentBuilderContextRequest';
import type { AgentBuilderRunResponse } from '../../core/api/generated/models/AgentBuilderRunResponse';
import type { AgentBuilderTriggerSuggestionsRequest } from '../../core/api/generated/models/AgentBuilderTriggerSuggestionsRequest';
import type { AgentBuilderTriggerSuggestionsResponse } from '../../core/api/generated/models/AgentBuilderTriggerSuggestionsResponse';
import type { AgentChannelConfigChannelTypeEnumKey } from '../../core/api/generated/models/AgentChannelConfig';
import type { AgentDeployment } from '../../core/api/generated/models/AgentDeployment';
import type { AgentDeploymentRequest } from '../../core/api/generated/models/AgentDeploymentRequest';
import type {
  AgentGoalGoalTypeEnumKey,
  AgentGoalStatusEnumKey,
} from '../../core/api/generated/models/AgentGoal';
import type { AgentGraphDefinition } from '../../core/api/generated/models/AgentGraphDefinition';
import type { AgentMissionEditorSessionRequest } from '../../core/api/generated/models/AgentMissionEditorSessionRequest';
import type { AgentMissionEditorSessionResponse } from '../../core/api/generated/models/AgentMissionEditorSessionResponse';
import type { AgentMissionValidationRequest } from '../../core/api/generated/models/AgentMissionValidationRequest';
import type { AgentMissionValidationResult } from '../../core/api/generated/models/AgentMissionValidationResult';
import type { AgentSkill } from '../../core/api/generated/models/AgentSkill';
import type { AgentSkillVersion } from '../../core/api/generated/models/AgentSkillVersion';
import type { AgentListResponse } from '../../core/api/generated/models/AgentListResponse';
import type { AgentToolDefinition } from '../../core/api/generated/models/AgentToolDefinition';
import type { AgentTriggerPolicy } from '../../core/api/generated/models/AgentTriggerPolicy';
import type { AgentUpsertRequest } from '../../core/api/generated/models/AgentUpsertRequest';
import type { AgentVersion } from '../../core/api/generated/models/AgentVersion';
import type { AgentVersionDraftRequest } from '../../core/api/generated/models/AgentVersionDraftRequest';
import type { Automation } from '../../core/api/generated/models/Automation';
import type { AutomationStepStepTypeEnumKey } from '../../core/api/generated/models/AutomationStep';
import type { AutomationTrigger } from '../../core/api/generated/models/AutomationTrigger';
import type { ChannelAccount } from '../../core/api/generated/models/ChannelAccount';
import type { Integration } from '../../core/api/generated/models/Integration';
import type { IntegrationsListChannelAccountsDataResponse } from '../../core/api/generated/models/IntegrationsListChannelAccountsDataResponse';
import type { IntegrationsListDataResponse } from '../../core/api/generated/models/IntegrationsListDataResponse';
import type { KnowledgeBaseListResponse } from '../../core/api/generated/models/KnowledgeBaseListResponse';
import type { ListAgentChannelConfigsResponse } from '../../core/api/generated/models/ListAgentChannelConfigsResponse';
import type { ListAgentGoalsResponse } from '../../core/api/generated/models/ListAgentGoalsResponse';
import type { ListAgentGraphDefinitionsResponse } from '../../core/api/generated/models/ListAgentGraphDefinitionsResponse';
import type { ListAgentMemoryEntriesResponse } from '../../core/api/generated/models/ListAgentMemoryEntriesResponse';
import type { ListAgentDeploymentsResponse } from '../../core/api/generated/models/ListAgentDeploymentsResponse';
import type { ListAgentRunsResponse } from '../../core/api/generated/models/ListAgentRunsResponse';
import type { ListAgentSkillsResponse } from '../../core/api/generated/models/ListAgentSkillsResponse';
import type { ListAgentSkillVersionsResponse } from '../../core/api/generated/models/ListAgentSkillVersionsResponse';
import type { ListAgentToolDefinitionsResponse } from '../../core/api/generated/models/ListAgentToolDefinitionsResponse';
import type { ListAgentTriggerPoliciesResponse } from '../../core/api/generated/models/ListAgentTriggerPoliciesResponse';
import type { ListAgentVersionsResponse } from '../../core/api/generated/models/ListAgentVersionsResponse';
import type { ListAutomationTriggersResponse } from '../../core/api/generated/models/ListAutomationTriggersResponse';
import type { ListAutomationsResponse } from '../../core/api/generated/models/ListAutomationsResponse';
import type { PublishAgentVersionRequest } from '../../core/api/generated/models/PublishAgentVersionRequest';
import type { ReplaceAgentTriggerPolicyAssignmentsRequest } from '../../core/api/generated/models/ReplaceAgentTriggerPolicyAssignmentsRequest';
import { BaseApiService } from '../../core/api/base-api.service';

export type AgentGoalInput = {
  id?: string;
  goalType: AgentGoalGoalTypeEnumKey;
  name: string;
  description?: string | null;
  successPolicy: Record<string, unknown>;
  metricPolicy: Record<string, unknown>;
  status: AgentGoalStatusEnumKey;
};

export type AgentChannelConfigInput = {
  id?: string;
  channelType: AgentChannelConfigChannelTypeEnumKey;
  enabled: boolean;
  behaviorPolicy: Record<string, unknown>;
};

export type AgentTriggerPolicyAssignmentRecord = {
  id: string;
  businessId: string;
  agentId: string;
  triggerPolicyId: string;
  sequence: number;
  status: 'active' | 'paused' | 'archived';
  overridePolicy: Record<string, unknown>;
  assignedByMembershipId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AgentTriggerPolicyInput = {
  id?: string;
  name: string;
  description?: string | null;
  status: 'active' | 'paused' | 'archived';
  scopePolicy: Record<string, unknown>;
  evaluationPolicy: Record<string, unknown>;
  rules: {
    id?: string;
    sequence: number;
    name?: string | null;
    conditionType: 'declarative' | 'ai_classifier' | 'hybrid';
    conditionPolicy: Record<string, unknown>;
    actionPolicy: Record<string, unknown>;
    rateLimitPolicy: Record<string, unknown>;
    status: 'active' | 'disabled' | 'archived';
  }[];
};

export type AgentToolDefinitionInput = {
  key: string;
  displayName: string;
  description: string;
  toolFamily: 'system' | 'integration_api_mirror' | 'custom_integration';
  owningDomain: string;
  integrationId?: string | null;
  providerKey?: string | null;
  endpointKey?: string | null;
  inputSchema: Record<string, unknown>;
  outputSchema?: Record<string, unknown> | null;
  requiredPermission: string;
  requiredEntitlementKey?: string | null;
  riskLevel: 'read' | 'low' | 'medium' | 'high' | 'irreversible';
  isIdempotent: boolean;
  businessConfigurable: boolean;
  defaultPayloadPolicy: Record<string, unknown>;
  authPolicy: Record<string, unknown>;
  requestPolicy: Record<string, unknown>;
  responseMapping: Record<string, unknown>;
  serviceAccountSafe: boolean;
  status: 'active' | 'deprecated' | 'disabled';
};

export type AgentSkillInput = {
  templateSource: 'platform_template' | 'business_custom' | 'template_copy';
  sourceTemplateId?: string | null;
  key: string;
  name: string;
  description?: string | null;
  category?: string | null;
  integrationProviderKey?: string | null;
  currentVersionId?: string | null;
  status: 'draft' | 'active' | 'archived';
};

export type AgentSkillVersionInput = {
  version: number;
  revision: number;
  status: 'draft' | 'active' | 'retired' | 'archived';
  inputSchema: Record<string, unknown>;
  outputSchema: Record<string, unknown>;
  contractHash: string;
  instructionPolicy: Record<string, unknown>;
  payloadPolicy: Record<string, unknown>;
  compositionPolicy: Record<string, unknown>;
  dependencyValidationStatus: 'pending' | 'valid' | 'invalid';
  tools: {
    sequence: number;
    stepType: 'tool' | 'skill';
    toolDefinitionId?: string | null;
    nestedSkillVersionId?: string | null;
    argumentPolicy: Record<string, unknown>;
    outputMapping: Record<string, unknown>;
    continueOnError: boolean;
  }[];
};

export type AutomationDefinitionInput = {
  id?: string;
  key?: string | null;
  name: string;
  description?: string | null;
  status: 'draft' | 'active' | 'paused' | 'archived';
  steps: {
    key: string;
    stepType: AutomationStepStepTypeEnumKey;
    config: Record<string, unknown>;
    loopPolicy?: Record<string, unknown>;
  }[];
  triggerDefaultsPolicy: Record<string, unknown>;
  defaultContextPolicy: Record<string, unknown>;
  errorPolicy: Record<string, unknown>;
  rateLimitPolicy: Record<string, unknown>;
};

export type AutomationTriggerInput = {
  id?: string;
  name?: string | null;
  status?: 'active' | 'paused' | 'disabled' | 'archived';
  triggerType?: 'manual' | 'scheduled' | 'conversation_message' | 'conversation_assignment' | 'crm_event' | 'revenue_event' | 'automation' | 'api';
  triggerPolicy?: Record<string, unknown>;
  channelAccountId?: string | null;
  deploymentId?: string | null;
  conversationContextPolicy?: Record<string, unknown>;
};

export type AutomationRunInput = {
  automationId: string;
  automationTriggerId?: string | null;
  triggerType: 'manual' | 'scheduled' | 'conversation_message' | 'conversation_assignment' | 'crm_event' | 'revenue_event' | 'automation' | 'api';
  input: Record<string, unknown>;
  contextRefs?: {
    id: string;
    domain: 'core' | 'crm' | 'conversations' | 'knowledge' | 'integrations' | 'revenue' | 'operations' | 'analytics' | 'agents';
    entityType: string;
    display?: string | null;
  }[];
  idempotencyKey?: string;
};

export type ArchetypeNodeType =
  | 'start'
  | 'load_context'
  | 'guardrail'
  | 'knowledge'
  | 'llm'
  | 'condition'
  | 'tool'
  | 'handoff'
  | 'response'
  | 'validate'
  | 'end';

export type ArchetypeGraphNode = {
  id: string;
  type: ArchetypeNodeType;
  name: string;
  protected: boolean;
  position: { x: number; y: number };
  config: Record<string, unknown>;
};

export type ArchetypeGraphEdge = {
  id: string;
  source: string;
  target: string;
  sourcePort?: string;
  condition?: Record<string, unknown>;
};

export type ArchetypeGraphSpec = {
  schemaVersion: 1;
  entryNodeId: string;
  nodes: ArchetypeGraphNode[];
  edges: ArchetypeGraphEdge[];
};

export type AgentArchetypeVersion = {
  id: string;
  archetypeId: string;
  businessId: string | null;
  version: number;
  revision: number;
  status: 'draft' | 'published' | 'deprecated';
  graphSpec: ArchetypeGraphSpec;
  goalPolicy: Record<string, unknown>;
  persona: Record<string, unknown>;
  channelPolicy: Record<string, unknown>;
  toolPolicy: Record<string, unknown>;
  retrievalPolicy: Record<string, unknown>;
  graphHash: string;
  validationStatus: 'pending' | 'valid' | 'invalid';
  validationReport: { issues?: ArchetypeValidationIssue[] };
  validatedGraphHash: string | null;
  testedGraphHash: string | null;
  lastValidatedAt: string | null;
  lastTestedAt: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ManagedAgentArchetype = {
  id: string;
  businessId: string | null;
  origin: 'system' | 'business';
  originArchetypeId: string | null;
  key: string;
  name: string;
  description: string | null;
  category: string | null;
  status: 'active' | 'disabled' | 'deprecated';
  currentPublishedVersionId: string | null;
  currentPublishedVersion: AgentArchetypeVersion | null;
  draftVersion: AgentArchetypeVersion | null;
  agentsUsingCount: number;
  versions?: AgentArchetypeVersion[];
  createdAt: string;
  updatedAt: string;
};

export type ArchetypeListResponse = {
  data: ManagedAgentArchetype[];
  meta: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
};

export type ArchetypeValidationIssue = {
  code: string;
  message: string;
  nodeId?: string;
  edgeId?: string;
};

export type ApprovedLlmModel = {
  id: string;
  provider: string;
  modelKey: string;
  displayName: string;
  description: string | null;
  contextWindow: number;
  supportsTools: boolean;
  supportsStructuredOutput: boolean;
  costTier: string;
  status: 'active';
};

export type AgentArchetypeUpdateAvailability = {
  available: boolean;
  reason?: string;
  archetypeId?: string;
  archetypeName?: string;
  currentVersion?: { id: string; version: number };
  targetVersion?: { id: string; version: number } | null;
};

@Injectable({ providedIn: 'root' })
export class AgentsApiService extends BaseApiService {
  protected override readonly resourcePath = 'v1';

  listAgents() {
    return this.get<AgentListResponse>('agents', {
      params: { pageSize: 100, order: 'desc', status: ['draft', 'active'] },
    });
  }

  createAgent(body: AgentUpsertRequest) {
    return this.post<Agent, AgentUpsertRequest>('agents', body);
  }

  getAgent(id: string) {
    return this.get<Agent>(['agents', id]);
  }

  updateAgent(id: string, body: Partial<AgentUpsertRequest>) {
    return this.patch<Agent, Partial<AgentUpsertRequest>>(['agents', id], body);
  }

  archiveAgent(id: string) {
    return this.post<Agent, null>(['agents', id, 'archive'], null);
  }

  listArchetypes() {
    return this.get<AgentArchetypeListResponse>('agents/archetypes');
  }

  listManagedArchetypes(params?: {
    q?: string;
    scope?: 'all' | 'system' | 'business';
    status?: Array<'active' | 'disabled' | 'deprecated'>;
  }) {
    return this.get<ArchetypeListResponse>('agents/archetypes', {
      params: { page: 1, pageSize: 100, ...(params ?? {}) },
    });
  }

  getArchetype(id: string) {
    return this.get<ManagedAgentArchetype>(['agents', 'archetypes', id]);
  }

  createArchetype(body: { name: string; description?: string | null; category?: string | null }) {
    return this.post<ManagedAgentArchetype, typeof body>('agents/archetypes', body);
  }

  updateArchetype(id: string, body: { name?: string; description?: string | null; category?: string | null }) {
    return this.patch<ManagedAgentArchetype, typeof body>(['agents', 'archetypes', id], body);
  }

  duplicateArchetype(id: string, body: { name?: string } = {}) {
    return this.post<ManagedAgentArchetype, typeof body>(['agents', 'archetypes', id, 'duplicate'], body);
  }

  archiveArchetype(id: string) {
    return this.post<ManagedAgentArchetype, null>(['agents', 'archetypes', id, 'archive'], null);
  }

  createArchetypeDraft(id: string) {
    return this.post<AgentArchetypeVersion, null>(['agents', 'archetypes', id, 'versions'], null);
  }

  saveArchetypeDraft(archetypeId: string, versionId: string, body: {
    expectedRevision: number;
    graphSpec: ArchetypeGraphSpec;
    goalPolicy?: Record<string, unknown>;
    persona?: Record<string, unknown>;
    channelPolicy?: Record<string, unknown>;
    toolPolicy?: Record<string, unknown>;
    retrievalPolicy?: Record<string, unknown>;
  }) {
    return this.patch<AgentArchetypeVersion, typeof body>(
      ['agents', 'archetypes', archetypeId, 'versions', versionId],
      body,
    );
  }

  validateArchetypeDraft(archetypeId: string, versionId: string, expectedRevision: number) {
    return this.post<{
      valid: boolean;
      graphHash: string;
      issues: ArchetypeValidationIssue[];
      version: AgentArchetypeVersion;
    }, { expectedRevision: number }>(
      ['agents', 'archetypes', archetypeId, 'versions', versionId, 'validate'],
      { expectedRevision },
    );
  }

  testArchetypeDraft(archetypeId: string, versionId: string, body: { expectedRevision: number; message: string }) {
    return this.post<{
      passed: boolean;
      output: string;
      trace: Array<{ nodeId: string; model: string; output: string }>;
      version: AgentArchetypeVersion;
    }, typeof body>(['agents', 'archetypes', archetypeId, 'versions', versionId, 'test'], body);
  }

  publishArchetypeDraft(archetypeId: string, versionId: string, body: {
    expectedRevision: number;
    idempotencyKey: string;
  }) {
    return this.post<AgentArchetypeVersion, typeof body>(
      ['agents', 'archetypes', archetypeId, 'versions', versionId, 'publish'],
      body,
    );
  }

  listApprovedLlmModels() {
    return this.get<{ data: ApprovedLlmModel[] }>('agents/llm-models');
  }

  getAgentArchetypeUpdate(agentId: string) {
    return this.get<AgentArchetypeUpdateAvailability>(['agents', agentId, 'archetype-update']);
  }

  applyAgentArchetypeUpdate(agentId: string, targetArchetypeVersionId: string) {
    return this.post<{
      draft: AgentVersion;
      fromVersion: { id: string; version: number };
      toVersion: { id: string; version: number };
      conflicts: Array<{ kind: string; nodeId: string; label: string }>;
      preserved: string[];
    }, { targetArchetypeVersionId: string }>(
      ['agents', agentId, 'archetype-update'],
      { targetArchetypeVersionId },
    );
  }

  listGraphDefinitions() {
    return this.get<ListAgentGraphDefinitionsResponse>('agent-graphs', {
      params: { status: ['active'] },
    });
  }

  listVersions(agentId: string) {
    return this.get<ListAgentVersionsResponse>(['agents', agentId, 'versions'], {
      params: { status: ['draft', 'published'], pageSize: 100 },
    });
  }

  upsertDraftVersion(agentId: string, body: AgentVersionDraftRequest) {
    return this.listVersions(agentId).pipe(
      switchMap((response) => {
        const draft = response.data.find((version) => version.status === 'draft');
        if (draft) {
          return this.patch<AgentVersion, AgentVersionDraftRequest>(
            ['agents', agentId, 'versions', draft.id],
            body,
          );
        }
        const published = response.data.find((version) => version.status === 'published');
        if (published) {
          return this.patch<AgentVersion, AgentVersionDraftRequest>(
            ['agents', agentId, 'versions', published.id],
            body,
          ).pipe(
            catchError((error: { status?: number }) =>
              error.status === 409
                ? this.createVersionDraft(agentId, body)
                : throwError(() => error),
            ),
          );
        }
        return this.createVersionDraft(agentId, body);
      }),
    );
  }

  listRuns(params?: { agentId?: string; pageSize?: number }) {
    return this.get<ListAgentRunsResponse>('agent-runs', {
      params: {
        pageSize: params?.pageSize ?? 50,
        ...(params?.agentId ? { agentId: params.agentId } : {}),
      },
    });
  }

  listMemoryForContact(contactId: string) {
    return this.get<ListAgentMemoryEntriesResponse>('agent-memory', {
      params: { pageSize: 50, scopeType: 'contact', contactId },
    });
  }

  listTools(params?: { integrationId?: string }) {
    return this.get<ListAgentToolDefinitionsResponse>('agent-tools', {
      params: {
        pageSize: 100,
        ...(params?.integrationId ? { integrationId: params.integrationId } : {}),
      },
    });
  }

  listSkills() {
    return this.get<ListAgentSkillsResponse>('agent-skills', {
      params: { pageSize: 100, status: ['active'] },
    });
  }

  createTool(body: AgentToolDefinitionInput) {
    return this.post<AgentToolDefinition, AgentToolDefinitionInput>('agent-tools', body);
  }

  createSkill(body: AgentSkillInput) {
    return this.post<AgentSkill, AgentSkillInput>('agent-skills', body);
  }

  listSkillVersions(skillId: string) {
    return this.get<ListAgentSkillVersionsResponse>(['agent-skills', skillId, 'versions']);
  }

  createSkillVersion(skillId: string, body: AgentSkillVersionInput) {
    return this.post<AgentSkillVersion, AgentSkillVersionInput>(
      ['agent-skills', skillId, 'versions'],
      body,
    );
  }

  listKnowledgeBases() {
    return this.get<KnowledgeBaseListResponse>('knowledge/bases', {
      params: { pageSize: 100, status: ['active'] },
    });
  }

  listIntegrations() {
    return this.get<IntegrationsListDataResponse>('integrations', {
      context: silentApiRequestContext(),
    }).pipe(catchError(() => of({ data: [] } as IntegrationsListDataResponse)));
  }

  listAllChannelAccounts(): Observable<ChannelAccount[]> {
    return this.get<IntegrationsListChannelAccountsDataResponse>('integrations/channels', {
      context: silentApiRequestContext(),
      params: { status: 'active' },
    }).pipe(
      map((response) => response.data.filter((channel) => channel.status === 'active')),
      catchError(() => of([] as ChannelAccount[])),
    );
  }

  replaceChannelConfigs(agentId: string, data: AgentChannelConfigInput[]) {
    return this.put<{ data: unknown[] }, { data: AgentChannelConfigInput[] }>(
      ['agents', agentId, 'channels'],
      { data },
    );
  }

  listChannelConfigs(agentId: string) {
    return this.get<ListAgentChannelConfigsResponse>(['agents', agentId, 'channels']);
  }

  replaceGoals(agentId: string, data: AgentGoalInput[]) {
    return this.put<{ data: unknown[] }, { data: AgentGoalInput[] }>(['agents', agentId, 'goals'], {
      data,
    });
  }

  listGoals(agentId: string) {
    return this.get<ListAgentGoalsResponse>(['agents', agentId, 'goals']);
  }

  createVersionDraft(agentId: string, body: AgentVersionDraftRequest) {
    return this.post<AgentVersion, AgentVersionDraftRequest>(['agents', agentId, 'versions'], body);
  }

  publishVersion(
    agentId: string,
    versionId: string,
    body: PublishAgentVersionRequest,
    idempotencyKey?: string,
  ) {
    return this.post<AgentVersion, PublishAgentVersionRequest>(
      ['agents', agentId, 'versions', versionId, 'publish'],
      body,
      idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {},
    );
  }

  listTriggerPolicies() {
    return this.get<ListAgentTriggerPoliciesResponse>('agent-trigger-policies', {
      params: { pageSize: 100 },
    });
  }

  createTriggerPolicy(body: AgentTriggerPolicyInput) {
    return this.post<AgentTriggerPolicy, AgentTriggerPolicyInput>('agent-trigger-policies', body);
  }

  updateTriggerPolicy(policyId: string, body: Partial<Omit<AgentTriggerPolicyInput, 'id'>>) {
    return this.patch<AgentTriggerPolicy, Partial<Omit<AgentTriggerPolicyInput, 'id'>>>(
      ['agent-trigger-policies', policyId],
      body,
    );
  }

  listTriggerPolicyAssignments(agentId: string) {
    return this.get<{ data: AgentTriggerPolicyAssignmentRecord[] }>([
      'agents',
      agentId,
      'trigger-policy-assignments',
    ]);
  }

  replaceTriggerPolicyAssignments(
    agentId: string,
    data: ReplaceAgentTriggerPolicyAssignmentsRequest['data'],
  ) {
    return this.put<unknown, ReplaceAgentTriggerPolicyAssignmentsRequest>(
      ['agents', agentId, 'trigger-policy-assignments'],
      { data },
    );
  }

  listDeployments(agentId: string) {
    return this.get<ListAgentDeploymentsResponse>('agent-deployments', {
      params: {
        agentId,
        bindingType: 'channel_account',
        configuredFromDomain: 'integrations',
        status: ['active', 'paused', 'disabled'],
      },
    });
  }

  createDeployment(body: AgentDeploymentRequest) {
    return this.post<AgentDeployment, AgentDeploymentRequest>('agent-deployments', body);
  }

  updateDeployment(deploymentId: string, patch: Partial<AgentDeploymentRequest>) {
    return this.patch<AgentDeployment, Partial<AgentDeploymentRequest>>(
      ['agent-deployments', deploymentId],
      patch,
    );
  }

  startBuilderMissionRun(agentId: string, body: AgentBuilderContextRequest) {
    return this.post<AgentBuilderRunResponse, AgentBuilderContextRequest>(
      ['agents', agentId, 'builder', 'mission-runs'],
      body,
    );
  }

  validateMission(body: AgentMissionValidationRequest) {
    return this.post<AgentMissionValidationResult, AgentMissionValidationRequest>(
      ['agents', 'builder', 'mission', 'validate'],
      body,
    );
  }

  createMissionEditorSession(agentId: string, body: AgentMissionEditorSessionRequest) {
    return this.post<AgentMissionEditorSessionResponse, AgentMissionEditorSessionRequest>(
      ['agents', agentId, 'builder', 'mission-editor', 'session'],
      body,
    );
  }

  suggestBuilderTriggers(body: AgentBuilderTriggerSuggestionsRequest) {
    return this.post<AgentBuilderTriggerSuggestionsResponse, AgentBuilderTriggerSuggestionsRequest>(
      ['agents', 'builder', 'trigger-suggestions'],
      body,
    );
  }

  graphKey(graphs: readonly AgentGraphDefinition[]) {
    return (
      graphs.find((graph) => graph.key === 'conversational-agent')?.key ??
      graphs.find((graph) => graph.key === 'conversation-agent')?.key ??
      graphs[0]?.key ??
      'conversational-agent'
    );
  }

  listAutomations() {
    return this.get<ListAutomationsResponse>('automations', {
      params: { pageSize: 100, order: 'desc' },
    });
  }

  createAutomation(body: AutomationDefinitionInput) {
    return this.post<Automation, AutomationDefinitionInput>('automations', body);
  }

  updateAutomation(automationId: string, body: Partial<AutomationDefinitionInput>) {
    return this.patch<Automation, Partial<AutomationDefinitionInput>>(
      ['automations', automationId],
      body,
    );
  }

  listAutomationTriggers(automationId: string) {
    return this.get<ListAutomationTriggersResponse>(['automations', automationId, 'triggers']);
  }

  createAutomationTrigger(automationId: string, body: AutomationTriggerInput) {
    return this.post<AutomationTrigger, AutomationTriggerInput>(
      ['automations', automationId, 'triggers'],
      body,
    );
  }

  updateAutomationTrigger(automationId: string, triggerId: string, body: Partial<AutomationTriggerInput>) {
    return this.patch<AutomationTrigger, Partial<AutomationTriggerInput>>(
      ['automations', automationId, 'triggers', triggerId],
      body,
    );
  }

  createAutomationRun(body: AutomationRunInput, idempotencyKey?: string) {
    return this.post<unknown, AutomationRunInput>(
      'automation-runs',
      body,
      idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {},
    );
  }

  resolveCurrentSkillVersions(
    skills: readonly AgentSkill[],
  ): Observable<Record<string, AgentSkillVersion>> {
    const skillsWithVersion = skills.filter((skill) => skill.currentVersionId);
    if (skillsWithVersion.length === 0) {
      return of({});
    }

    return forkJoin(
      skillsWithVersion.map((skill) =>
        this.get<ListAgentSkillVersionsResponse>(['agent-skills', skill.id, 'versions'], {
          context: silentApiRequestContext(),
        }).pipe(
          map((response) => response.data.find((version) => version.id === skill.currentVersionId)),
          catchError(() => of(undefined)),
        ),
      ),
    ).pipe(
      map((versions) => {
        const bySkillId: Record<string, AgentSkillVersion> = {};
        versions.forEach((version) => {
          if (version) bySkillId[version.agentSkillId] = version;
        });
        return bySkillId;
      }),
    );
  }

  providerKeyForIntegration(integration: Integration | undefined) {
    return integration?.provider ?? 'custom';
  }
}
